/**
 * Centralized role definitions for the application.
 *
 * This module is the single source of truth for all user roles and permission groups.
 */

export const UserRole = {
  ADMIN: 'admin',
  PILOT: 'pilot',
  MAINTENANCE: 'maintenance',
  AIRSPACE_OBSERVER: 'airspace_observer',
} as const;

export type UserRoleType = (typeof UserRole)[keyof typeof UserRole];

export const VALID_ROLES: UserRoleType[] = Object.values(UserRole);

// Permission groups
export const MAINTENANCE_MANAGERS: UserRoleType[] = [UserRole.ADMIN, UserRole.MAINTENANCE];

// Helper functions
export function hasRole(roles: string[] | undefined, role: UserRoleType): boolean {
  return roles?.includes(role) ?? false;
}

export function hasAnyRole(roles: string[] | undefined, required: UserRoleType[]): boolean {
  return required.some((r) => roles?.includes(r));
}

export function canManageMaintenance(roles: string[] | undefined): boolean {
  return hasAnyRole(roles, MAINTENANCE_MANAGERS);
}

export function isAdmin(roles: string[] | undefined): boolean {
  return hasRole(roles, UserRole.ADMIN);
}

// Role options for UI dropdowns (with translation keys)
export const ROLE_OPTIONS = [
  {
    value: UserRole.ADMIN,
    labelKey: 'adminToolsPage.roles.admin',
    descKey: 'adminToolsPage.roles.adminDesc',
  },
  {
    value: UserRole.PILOT,
    labelKey: 'adminToolsPage.roles.pilot',
    descKey: 'adminToolsPage.roles.pilotDesc',
  },
  {
    value: UserRole.MAINTENANCE,
    labelKey: 'adminToolsPage.roles.maintenance',
    descKey: 'adminToolsPage.roles.maintenanceDesc',
  },
  {
    value: UserRole.AIRSPACE_OBSERVER,
    labelKey: 'adminToolsPage.roles.airspaceObserver',
    descKey: 'adminToolsPage.roles.airspaceObserverDesc',
  },
] as const;
